// F2 upload flow and F9 exports, both in the browser.
import { strToU8, zipSync, type Zippable } from "fflate";
import type { Contract, DocumentMeta } from "../../shared/contract";
import { contractsToCsv } from "../../shared/csv";
import { todayIn } from "../../shared/dates";
import { ApiError, api } from "./api";
import type { ExtractResult } from "./extract";

export const MAX_FILE_BYTES = 20 * 1024 * 1024;

/** Checks a file before any network call, with messages for the usual mistakes. */
export function checkFile(file: File, maxBytes = MAX_FILE_BYTES): string | null {
  const name = file.name.toLowerCase();
  const dot = name.lastIndexOf(".");
  const ext = dot > 0 ? name.slice(dot + 1) : "";
  if (file.size === 0) return "This file is empty. Check you picked the right file.";
  if (ext === "doc") return "Old Word files (.doc) aren't supported. Save it as .docx or PDF first.";
  if (["jpg", "jpeg", "png", "heic", "webp", "gif"].includes(ext)) return "Photos aren't supported yet. Save or scan the contract as a PDF.";
  if (ext !== "pdf" && ext !== "docx") return "Only PDF and Word (.docx) files are supported.";
  if (file.size > maxBytes) return `This file is ${(file.size / 1024 / 1024).toFixed(1)} MB. Files can be up to ${Math.round(maxBytes / 1024 / 1024)} MB.`;
  return null;
}

interface UploadStart {
  document: DocumentMeta;
  upload: { method: string; url: string; headers: Record<string, string>; direct: boolean };
}

/** Uploads one file to a contract and stores its extracted text. Returns the ready document. */
export async function uploadFile(
  contractId: string,
  file: File,
  extracted: ExtractResult | null,
  onProgress: (fraction: number) => void = () => {},
): Promise<DocumentMeta> {
  const start = await api<UploadStart>(`/api/contracts/${contractId}/documents`, {
    method: "POST",
    json: { filename: file.name, size: file.size },
  });
  await putWithProgress(start.upload, file, onProgress);
  const done = start.upload.direct
    ? await api<{ document: DocumentMeta }>(`/api/documents/${start.document.id}/complete`, { method: "POST" })
    : null;
  const document = done?.document ?? start.document;
  if (extracted && extracted.text.trim()) {
    try {
      await api(`/api/documents/${document.id}/text`, { method: "PUT", json: { text: extracted.text, method: extracted.method } });
    } catch {
      // The file is safe; search just won't include its text.
    }
  }
  return { ...document, status: "ready" };
}

function putWithProgress(upload: UploadStart["upload"], file: File, onProgress: (f: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(upload.method, upload.url);
    for (const [k, v] of Object.entries(upload.headers)) xhr.setRequestHeader(k, v);
    if (!upload.direct) xhr.withCredentials = true;
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total);
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) return resolve();
      let message = `Upload failed (${xhr.status}).`;
      try {
        message = JSON.parse(xhr.responseText).error ?? message;
      } catch {
        // R2 answers in XML; keep the generic message.
      }
      reject(new ApiError(xhr.status, message, "upload_failed"));
    };
    xhr.onerror = () => reject(new ApiError(0, "The upload was interrupted. Check your connection and try again.", "offline"));
    xhr.send(file);
  });
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

const stamp = () => todayIn(Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC");

export function exportCsv(contracts: Contract[], ownerName: (id: string | null) => string) {
  downloadBlob(new Blob([contractsToCsv(contracts, stamp(), ownerName)], { type: "text/csv;charset=utf-8" }), `clausekeep-contracts-${stamp()}.csv`);
}

/** Folder-safe name for a contract inside the ZIP. */
export function zipFolderName(c: Pick<Contract, "id" | "title">): string {
  // eslint-disable-next-line no-control-regex
  const safe = c.title.replace(/[\u0000-\u001f<>:"/\\|?*]/g, "").replace(/\s+/g, " ").trim().slice(0, 60) || "Untitled";
  return `${safe} (${c.id.slice(0, 8)})`;
}

/**
 * F9 full export: contracts.csv, contracts.json and every file, zipped on the device
 * (store-only, PDFs don't compress). Files that fail to download are listed in errors.txt.
 */
export async function buildFullExport(
  contracts: Contract[],
  ownerName: (id: string | null) => string,
  onProgress: (done: number, total: number) => void = () => {},
  fetchFile: (doc: DocumentMeta) => Promise<Uint8Array> = async (doc) => {
    const res = await fetch(`/api/documents/${doc.id}/file`, { credentials: "same-origin" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return new Uint8Array(await res.arrayBuffer());
  },
): Promise<Uint8Array<ArrayBuffer>> {
  const live = contracts.filter((c) => !c.deletedAt);
  const files: Zippable = {
    "contracts.csv": strToU8(contractsToCsv(live, stamp(), ownerName)),
    "contracts.json": strToU8(JSON.stringify(live.map(({ ...c }) => c), null, 2)),
  };
  const docs = live.flatMap((c) => c.documents.map((d) => ({ c, d })));
  const errors: string[] = [];
  const used = new Set<string>();
  let done = 0;
  for (const { c, d } of docs) {
    let path = `files/${zipFolderName(c)}/${d.filename}`;
    for (let i = 2; used.has(path); i++) path = `files/${zipFolderName(c)}/${i}-${d.filename}`;
    used.add(path);
    try {
      files[path] = [await fetchFile(d), { level: 0 }];
    } catch (err) {
      errors.push(`${c.title} / ${d.filename}: ${err instanceof Error ? err.message : "failed"}`);
    }
    onProgress(++done, docs.length);
  }
  if (errors.length) files["errors.txt"] = strToU8(`These files couldn't be downloaded:\n${errors.join("\n")}\n`);
  return zipSync(files, { level: 6 }) as Uint8Array<ArrayBuffer>;
}
